import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const SOPsModule = ({ onBack, currentUser }) => {
  const [sops, setSops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSops, setFilteredSops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSop, setSelectedSop] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    filename: '',
    file_data: '',
    description: '',
  });
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const fileInputRef = useRef(null);

  useEffect(() => { fetchSops(); }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSops(sops);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredSops(sops.filter(s =>
        s.title.toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q) ||
        s.filename.toLowerCase().includes(q)
      ));
    }
  }, [sops, searchQuery]);

  const fetchSops = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer-care/sops`);
      setSops(res.data);
    } catch (err) {
      console.error('Error fetching SOPs:', err);
      setSops([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { alert('Please select a PDF file only.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('File size must be less than 10MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setUploadForm({
        ...uploadForm,
        filename: file.name,
        file_data: base64,
        title: uploadForm.title || file.name.replace('.pdf', ''),
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.file_data) { alert('Please select a PDF file.'); return; }
    if (!uploadForm.title.trim()) { alert('Please enter a title.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/customer-care/sops`, uploadForm);
      alert('SOP uploaded successfully!');
      setUploadForm({ title: '', filename: '', file_data: '', description: '' });
      setShowUploadForm(false);
      fetchSops();
    } catch (err) {
      alert('Failed to upload SOP: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (sop) => {
    window.open(`${API}/customer-care/sops/${sop.id}/view`, '_blank');
  };

  const handleDownload = (sop) => {
    const link = document.createElement('a');
    link.href = `${API}/customer-care/sops/${sop.id}/download`;
    link.download = sop.filename;
    link.click();
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/customer-care/sops/${selectedSop.id}`, editForm);
      alert('SOP updated successfully!');
      setShowEditModal(false);
      fetchSops();
    } catch (err) {
      alert('Failed to update SOP.');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customer-care/sops/${selectedSop.id}`);
      alert('SOP deleted.');
      setShowDeleteModal(false);
      fetchSops();
    } catch (err) {
      alert('Failed to delete SOP.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-6">
          ← Back
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="sops-title">Standard Operating Procedures</h1>
            <p className="text-gray-500 text-sm mt-1">Upload and manage SOPs for Customer Care personnel.</p>
          </div>
          <button
            onClick={() => setShowUploadForm(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm"
            data-testid="sops-upload-btn"
          >
            + Upload SOP
          </button>
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Upload New SOP</h2>
            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="SOP title..."
                  data-testid="sop-upload-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20"
                  placeholder="Brief description..."
                  data-testid="sop-upload-description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PDF File *</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="text-sm"
                  data-testid="sop-upload-file"
                />
                {uploadForm.filename && (
                  <p className="text-xs text-green-600 mt-1">Selected: {uploadForm.filename}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="sop-upload-submit">
                  {loading ? 'Uploading...' : 'Upload SOP'}
                </button>
                <button type="button" onClick={() => setShowUploadForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search SOPs..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm"
            data-testid="sops-search"
          />
        </div>

        {/* SOPs List */}
        {loading && !showUploadForm ? (
          <p className="text-center text-gray-400 py-8">Loading SOPs...</p>
        ) : filteredSops.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-400 text-lg mb-2">{searchQuery ? 'No SOPs match your search.' : 'No SOPs uploaded yet.'}</p>
            <p className="text-gray-400 text-sm">Click "+ Upload SOP" to add your first procedure.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSops.map((sop) => (
              <div key={sop.id} className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between hover:shadow-sm transition-shadow" data-testid={`sop-item-${sop.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <h3 className="font-semibold text-gray-800 truncate">{sop.title}</h3>
                  {sop.description && <p className="text-xs text-gray-500 truncate mt-0.5">{sop.description}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{sop.filename} — {new Date(sop.created_date).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleView(sop)} className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100" data-testid={`sop-view-${sop.id}`}>View</button>
                  <button onClick={() => handleDownload(sop)} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" data-testid={`sop-download-${sop.id}`}>Download</button>
                  <button onClick={() => { setSelectedSop(sop); setEditForm({ title: sop.title, description: sop.description || '' }); setShowEditModal(true); }} className="px-3 py-1.5 text-xs font-medium bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100" data-testid={`sop-edit-${sop.id}`}>Edit</button>
                  <button onClick={() => { setSelectedSop(sop); setShowDeleteModal(true); }} className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100" data-testid={`sop-delete-${sop.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedSop && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h2 className="text-lg font-bold mb-4">Edit SOP</h2>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="sop-edit-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20" data-testid="sop-edit-description" />
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                  <button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-lg text-sm font-semibold" data-testid="sop-edit-submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteModal && selectedSop && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4">
              <h2 className="text-lg font-bold mb-2">Delete SOP</h2>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete "{selectedSop.title}"?</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-semibold" data-testid="sop-delete-confirm">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SOPsModule;
